/**
 * useLogin Hook
 * Custom hook for handling login logic
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { wait, encode, decode, C, storage } from '@/utils';
import { connect, getSocket } from '@/utils/socketInstance';
import { useToast } from './useToast';

export const useLogin = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showTwoFactorInput, setShowTwoFactorInput] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempLoginData, setTempLoginData] = useState(null);
  const loginTimeoutRef = useRef(null);

  const check2FAStatus = async (uid) => {
    try {
      const response = await fetch(`https://api.jackopot.com/2fa/status/${uid}`);
      const data = await response.json();
      return data.isEnabled;
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      return false;
    }
  };

  const setLogin = (data) => {
    console.log('Setting login:', data);
    if (data.status === true) {
      showSuccess('Login successful! Redirecting...');
      Cookies.set("session", data.token, { expires: 14 });
      Cookies.set("uid", data.uid);
      storage.setKey('logged', true);
      storage.setKey('token', data.token);
      storage.setKey('name', data.name);
      storage.setKey('avatar', data.avatar);
      storage.setKey('email', data.email);
      storage.setKey('credit', data.credit);
      storage.setKey('room', data.room);
      storage.setKey('friends', data.friends);
      storage.setKey('country', data.country);
      localStorage.setItem('showLoginSuccessModal', 'true');
      wait(1000).then(() => {
        navigate('/');
        window.location.reload();
      });
    } else {
      setIsLoading(false);
      showError(data.status || 'Login failed. Please try again.');
    }
  };

  const handleLoginResponse = async (data) => {
    console.log('handleLoginResponse called with:', data);
    
    // Clear timeout when response arrives
    if (loginTimeoutRef.current) {
      clearTimeout(loginTimeoutRef.current);
      loginTimeoutRef.current = null;
    }

    // Check if data is null or invalid
    if (!data || typeof data !== 'object') {
      console.error('Invalid login response data:', data);
      setIsLoading(false);
      showError('Invalid response from server. Please try again.');
      return;
    }

    if (data.status === true) {
      try {
        const is2FAEnabled = await check2FAStatus(data.uid);
        if (is2FAEnabled) {
          setShowTwoFactorInput(true);
          setTempLoginData(data);
          setIsLoading(false);
        } else {
          setLogin(data);
        }
      } catch (error) {
        setIsLoading(false);
        showError('Error verifying account security');
      }
    } else {
      setIsLoading(false);
      showError(data.status || 'Login failed. Please try again.');
    }
  };

  // Setup socket listeners
  useEffect(() => {
    // Get current socket instance
    let currentSocket = getSocket();
    if (!currentSocket) {
      currentSocket = connect(storage.getKey('token'));
    }

    // Simple socket event listeners like the working code
    if (currentSocket) {
      console.log('Setting up login listener on socket:', currentSocket.id || 'pending');
      currentSocket.on(C.LOGIN_USER, data => {
        console.log('Received login response data (raw):', data);
        console.log('Data type:', typeof data, 'Is ArrayBuffer:', data instanceof ArrayBuffer);
        const decoded = decode(data);
        console.log('Decoded login response:', decoded);
        handleLoginResponse(decoded);
      });
    } else {
      console.error('Socket not available for login listener');
    }

    return () => {
      if (currentSocket) {
        currentSocket.off(C.LOGIN_USER);
      }
    };
  }, []);

  const handleTwoFactorSubmit = async (twoFactorCode) => {
    if (!twoFactorCode) {
      showError('Please enter verification code');
      return false;
    }

    setIsLoading(true);

    try {
      const response = await fetch('https://api.jackopot.com/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: tempLoginData.uid,
          token: twoFactorCode
        })
      });
      const data = await response.json();
      if (data.success) {
        setLogin({
          ...tempLoginData,
          status: true
        });
        return true;
      } else {
        setIsLoading(false);
        showError('Invalid verification code');
        return false;
      }
    } catch (error) {
      setIsLoading(false);
      showError('Failed to verify code');
      return false;
    }
  };

  const handleLoginSubmit = async (username, password) => {
    setIsLoading(true);

    // Add timeout to prevent stuck loading state (15 seconds)
    loginTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      showError('Login request timed out. Please check your connection and try again.');
    }, 15000);

    // Ensure socket is available - like working code
    let currentSocket = getSocket();
    if (!currentSocket) {
      currentSocket = connect(storage.getKey('token'));
    }

    if (!currentSocket) {
      setIsLoading(false);
      showError('Connection error. Please refresh the page.');
      return false;
    }

    // Simple emit like the working code
    try {
      await wait(200);
      const payload = { 
        username: username.toLowerCase(), 
        password, 
        recaptcha: 'google' 
      };
      console.log('Emitting login:', payload);
      console.log('Socket:', currentSocket);
      console.log('Socket connected:', currentSocket?.connected);
      const encoded = encode(payload);
      console.log('Encoded payload:', encoded);
      currentSocket.emit(C.LOGIN_USER, encoded);
      console.log('Emit sent');
      return true;
    } catch (error) {
      console.error('Error in wait/emit:', error);
      if (loginTimeoutRef.current) {
        clearTimeout(loginTimeoutRef.current);
        loginTimeoutRef.current = null;
      }
      setIsLoading(false);
      showError('Connection error. Please try again.');
      return false;
    }
  };

  return {
    isLoading,
    setIsLoading,
    showTwoFactorInput,
    setShowTwoFactorInput,
    twoFactorCode,
    setTwoFactorCode,
    tempLoginData,
    handleLoginSubmit,
    handleTwoFactorSubmit,
    showError,
    showInfo,
  };
};

export default useLogin;
